/* eslint-disable no-shadow */
/* eslint-disable no-return-assign */
/* eslint-disable no-underscore-dangle */
import useTranslation from 'next-translate/useTranslation'

import {
  SearchJobsQuery,
  SearchJobsDocument,
  Job,
  QuerySearchJobsArgs,
  OrderDirection,
  JobsSearchOrderField,
  JobsSearchOrderBy,
  Company,
  LocaleEnum,
  Metadata,
  PageEnum,
  MetadataQueryVariables,
  MetadataQuery,
  MetadataDocument,
} from 'generated/apolloComponents'
import { useContext, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { HeartIcon } from '@heroicons/react/24/solid'
import JobListItemWrapper from '@/components/molecules/JobListItem/JobListItemWrapper'
import { initializeApollo } from '@/api/apollo'
import MultiSelect, { SelectDataType } from '@/components/atoms/Select/Multi'
import JobsFilters from '@/components/organisms/JobFilters/JobFilters'

import {
  arrayOfObjectToStringArrayByKey,
  arrayToObject,
  isEmpty,
} from '@/helpers/utils'
import Pagination from '@/components/molecules/Pagination'
import FiltersList from '@/components/molecules/FiltersList'
import { sorting } from '@/data/sortingOptions'
import {
  ActionType,
  convertJobsFiltersSlugToURL,
  convertJobStateFiltersToQueryFilters,
  JobListContext,
  JobStateFilters,
  OrderStateType,
} from '@/context/jobListContext'
import Button from '../atoms/Button'
import { useMe } from '@/context/meContext'
import JobFiltersHeader from '../organisms/JobFiltersHeader'
import JobFiltersFooterMobile from '../organisms/JobFiltersFooterMobile'
import JobListIcons from '../molecules/JobListIcons'
import GSListItem from '../molecules/JobListItem/GSListItem'
import JobLinks from '../organisms/JobLinks'
import Banner from '../molecules/Banner'

interface iProps {
  jobs: Job[]
  jobsLocations: string[]
  totalCount: number
  slugState?: any
  company?: Company
  domainLang?: string
  metaData?: Metadata
}

const JobList = ({
  jobs,
  jobsLocations,
  totalCount,
  slugState,
  company,
  domainLang,
  metaData,
}: iProps) => {
  const router = useRouter()
  const [jobsList, setJobsList] = useState<Job[]>(jobs)
  const [jobsCount, setJobsCount] = useState(totalCount)
  const isMounted = useRef(false)
  const { show } = router.query
  const [isVisible, setIsVisible] = useState(show === 'filters')
  const { state, dispatch } = useContext(JobListContext)
  const { t, lang } = useTranslation()
  const [companyHasOffers, setCompanyHasOffers] = useState<boolean | null>(
    company ? null : true
  )
  const dynamicRoute = useRouter().asPath
  const { me, loadMe } = useMe()

  const queryLanguage = LocaleEnum[(domainLang || lang) as 'pl' | 'en']
  const [minSalaryFilter, setMinSalaryFilter] = useState<number | null>(null)
  const [header, setHeader] = useState(metaData?.header)

  const pressesGeneratorLink = () => {
    const prefixString = '/admin/shortener/jobs'

    return (
      prefixString +
      convertJobsFiltersSlugToURL(state, true, (e: any) =>
        encodeURIComponent(e)
      )
    )
  }

  useEffect(() => {
    if (router.asPath === '/companies/jobs') {
      dispatch({
        type: ActionType.ChangeFilter,
        payload: { experienceLevel: [], city: [], role: [], skills: [] },
      })
    }
  }, [dynamicRoute])

  useEffect(() => {
    const slug = convertJobsFiltersSlugToURL(state)

    const loadData = async function () {
      const apolloClient = initializeApollo({})
      if (slugState) {
        router.push({
          pathname: `/companies/jobs${slug}`,
        })
      }
      let filters = convertJobStateFiltersToQueryFilters(
        state.filters as JobStateFilters
      )
      const slugFilters = filters

      if (company) {
        filters = { company: [company.id], ...filters }
      }

      const variables: QuerySearchJobsArgs = {
        page: state.page,
        perPage: state.perPage,
        filters: filters,
        language: queryLanguage,
      }
      if (me && me.rejectedJobs) variables.exclude = [...me.rejectedJobs]
      const metaVariables: MetadataQueryVariables = {
        pageType: PageEnum.JobList,
        language: queryLanguage,
        jobFilters: filters,
      }
      const currentMetaData = await apolloClient.query<
        MetadataQuery,
        MetadataQueryVariables
      >({
        query: MetadataDocument,
        variables: metaVariables,
      })
      setHeader(currentMetaData.data.metadata?.header)
      if (
        state.order &&
        state.order.value &&
        (state.order.value as JobsSearchOrderBy).field !== 'DEFAULT'
      )
        variables.order = state.order.value as JobsSearchOrderBy

      const jobsData = await apolloClient.query<
        SearchJobsQuery,
        QuerySearchJobsArgs
      >({
        query: SearchJobsDocument,
        variables: variables,
      })

      let jobsUniqueByKey: any = []
      let jobsByCityArray: any = []
      let concatJobs = []
      concatJobs = [...jobsData.data.searchJobs.nodes!]

      if (company) {
        let jobLocationFilters = {}
        if (company && company.locations) {
          const cityKey = queryLanguage === 'pl' ? 'cityPl' : 'cityEn'
          const cities = arrayOfObjectToStringArrayByKey(
            company.locations,
            cityKey
          )
          jobLocationFilters = { city: cities, ...slugFilters }
        }
        const variablesTwo: QuerySearchJobsArgs = {
          page: 1,
          perPage: 10,
          filters: jobLocationFilters,
          language: queryLanguage,
        }

        if (me && me.rejectedJobs) variablesTwo.exclude = [...me.rejectedJobs]

        const jobsByCityData = await apolloClient.query<
          SearchJobsQuery,
          QuerySearchJobsArgs
        >({
          query: SearchJobsDocument,
          variables: variablesTwo,
        })

        jobsByCityArray = jobsByCityData.data.searchJobs.nodes
        if (companyHasOffers === null) {
          jobsData.data.searchJobs.nodes!.length < 10
            ? setCompanyHasOffers(false)
            : setCompanyHasOffers(true)
        }
      }
      concatJobs = [...jobsData.data.searchJobs.nodes!]

      jobsUniqueByKey = [
        // @ts-ignore
        ...new Map(concatJobs.map((item) => [item.id, item])).values(),
      ]

      setJobsList(jobsUniqueByKey)
      setJobsCount(jobsData.data.searchJobs.totalCount)
    }
    isMounted.current ? loadData() : (isMounted.current = true)
  }, [state, me])

  useEffect(() => {
    loadMe(true)
  }, [state])

  useEffect(() => {
    if (slugState && !isEmpty(slugState)) {
      dispatch({ type: ActionType.SetState, payload: slugState })
    }
  }, [])

  const handlePageChange = (page: number) => {
    dispatch({ type: ActionType.ChangePage, payload: page })
  }

  const handleOrderChange = (value: SelectDataType | null, key: string) => {
    if (value) {
      let sorting = null
      if (typeof value.value === 'string') {
        sorting = value.value.split(',')
        sorting = {
          direction: sorting[1] as OrderDirection,
          field: sorting[0] as JobsSearchOrderField,
        }
        const tmp: OrderStateType = {
          label: value.label,
          value: sorting || value.value,
        }

        dispatch({ type: ActionType.ChangeOrder, payload: tmp })
      }
    }
  }

  const orderWithLabel = (order: OrderStateType) => {
    if (!order.value) return null

    const labelName = sorting.find(
      (item) => item.value === `${order.value.field},${order.value.direction}`
    )?.label

    return {
      label: t(`common:${labelName}`),
      value: order.value,
    }
  }

  useEffect(() => {
    state.filters?.salaryBrackets &&
      setMinSalaryFilter(state.filters.salaryBrackets.min)
  }, [state])

  return (
    <>
      <JobFiltersHeader
        jobsLocations={arrayToObject(jobsLocations)}
        slugState={slugState}
        setIsVisible={setIsVisible}
      />
      <JobsFilters
        jobsLocations={arrayToObject(jobsLocations)}
        slugState={slugState}
        totalCount={jobsCount}
        handleOrderChange={handleOrderChange}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
      />
      <div className='bg-gray-bg py-8 relative min-h-[calc(100dvh-500px)]'>
        <Banner />
        <div className='container'>
          <div className='mb-6 md:mb-10'>
            <div className='flex gap-8 items-center'>
              <div className='mt-2 grow'>
                {header ? (
                  <h1
                    className='text-3xl font-bold'
                    dangerouslySetInnerHTML={{ __html: header }}
                  />
                ) : null}
                <div className='md:flex mt-4 md:mt-6'>
                  <FiltersList state={state} dispatch={dispatch} />
                </div>
              </div>
              {me && me.likedJobs && me.likedJobs.length > 0 && (
                <div className='ml-auto hidden md:block'>
                  <a href='/companies/jobs/saved'>
                    <button type='button' className='button-black-clear'>
                      <HeartIcon className='mr-2 w-6 text-neutral-500' />
                      {t('search:favourite')}
                    </button>
                  </a>
                </div>
              )}
              <div className='ml-auto'>
                <MultiSelect
                  state={state}
                  type='single'
                  value={
                    (state.order && orderWithLabel(state.order)) || {
                      label: t('common:default'),
                      value: { direction: 'ASC', field: 'DEFAULT' },
                    }
                  }
                  name='order'
                  onSelect={handleOrderChange}
                  data={sorting.map(
                    (item) =>
                      (item = { ...item, label: t(`common:${item.label}`) })
                  )}
                  title={t('common:sort')}
                  className='flex-1 w-96 multiSelect--small hidden md:block'
                />
                {me && me.__typename === 'Admin' ? (
                  <Button
                    className='w-full mt-3'
                    href={pressesGeneratorLink()}
                    type='primary'
                  >
                    Generuj prasówkę
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
          <JobListIcons />
          {jobsList.length > 0 &&
            jobsList.map((job) => (
              <JobListItemWrapper
                key={job.id}
                job={job}
                me={me!}
                minSalaryFilter={minSalaryFilter}
              />
            ))}
          {jobsList.length === 0 && (
            <div className='text-center my-24'>
              <div className='font-bold text-lg'>{t('search:no_results')}</div>
              <div className='mt-4'>{t('search:change_filters')}</div>
            </div>
          )}
        </div>
        <Pagination
          className='pagination-bar'
          currentPage={state.page}
          totalCount={jobsCount}
          pageSize={state.perPage}
          onPageChange={(page: number) => handlePageChange(page)}
        />
      </div>
      <JobLinks locations={jobsLocations} filters={state.filters} />
      <JobFiltersFooterMobile
        handleOrderChange={handleOrderChange}
        slugState={slugState}
        setIsVisible={setIsVisible}
        totalCount={totalCount}
        me={me}
      />
    </>
  )
}

export default JobList
